import React, { useEffect, useRef } from 'react';

import { Textarea as BaseTextarea, TextareaOverrides } from 'baseui/textarea';
import PropTypes from 'prop-types';

import { Caption } from 'shared/components/ihcl/input';

type TextareaProps = {
  caption?: string;
  error?: boolean;
  instanceId?: string;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  autoResize?: boolean;
  value: string;
  overrides?: TextareaOverrides;
  [key: string]: any;
};

export const Textarea = ({
  caption = null,
  error = false,
  instanceId = null,
  autoResize = false,
  ...props
}: TextareaProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!autoResize || !textareaRef.current) return;

    const el = textareaRef.current;

    const resize = () => {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    };
    resize();
    const listener = () => resize();
    el.addEventListener('input', listener);

    // eslint-disable-next-line consistent-return
    return () => {
      el.removeEventListener('input', listener);
    };
  }, [autoResize, props.value]); // re-run on value change if controlled

  return (
    <>
      <BaseTextarea
        {...props}
        inputRef={autoResize ? textareaRef : undefined}
        overrides={{
          Input: {
            props: {
              ...(instanceId && { 'data-instance-id': instanceId }),
              ...props.overrides?.Input?.props,
            },
            style: {
              ...props.overrides?.Input?.style,
            },
          },
          InputContainer: props.overrides?.InputContainer,
          Root: props.overrides?.Root,
        }}
      />
      {caption && <Caption $error={error}>{caption}</Caption>}
    </>
  );
};

Textarea.propTypes = {
  caption: PropTypes.string,
  error: PropTypes.bool,
  instanceId: PropTypes.string,
};
